/**
 * Fred Martin Collection - Product Form Component
 * @version 2.0.0 | Build 20250105.1430
 * @author Fred Martin Studio
 * @description Product form handling with cart integration
 * Custom implementation for Fred Martin Collection theme
 */

(() => {
  if (customElements.get('product-form')) {
    return;
  }

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.form.querySelector('[name="add"]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.productBar = document.querySelector(
        `#ProductBar-${this.dataset.sectionId}`
      );

      this.form.addEventListener(
        'submit',
        this.onSubmitHandler.bind(this)
      );

      document.addEventListener('globo.preorder.show.addtocart', () => {
        const selector = document.querySelector(`product-selector[data-section-id="${this.dataset.sectionId}"]`);
        const available = selector?.currentVariant?.available ?? this.dataset.available === 'true';
        this.toggleAddButton(!available, available ? '' : window.variantStrings.soldOut);
      });

      this.restockObserver = new MutationObserver(() => this.syncRestockContent());
      this.restockObserver.observe(this, { childList: true, subtree: true });
      document.addEventListener('globo.preorder.show.bis', () => this.syncRestockContent());
      document.addEventListener('fm:size-unit-change', () => this.syncRestockContent());

      this.toggleAddButton(this.dataset.available === 'false', window.variantStrings.soldOut);
    }

    disconnectedCallback() {
      this.restockObserver?.disconnect();
    }

    syncRestockContent() {
      const messages = {
        en: { title: size => size ? `Size ${size} is currently out of stock.` : 'This item is currently out of stock.', button: 'Notify me', email: 'Email address' },
        es: { title: size => size ? `La talla ${size} está agotada.` : 'Este artículo está agotado.', button: 'Avísame', email: 'Correo electrónico' },
        fr: { title: size => size ? `La pointure ${size} est actuellement épuisée.` : 'Cet article est actuellement épuisé.', button: 'Prévenez-moi', email: 'Adresse e-mail' },
        de: { title: size => size ? `Größe ${size} ist derzeit ausverkauft.` : 'Dieser Artikel ist derzeit ausverkauft.', button: 'Benachrichtige mich', email: 'E-Mail-Adresse' }
      };
      const copy = messages[this.dataset.restockLocale] || messages.en;
      const selector = document.querySelector(`product-selector[data-section-id="${this.dataset.sectionId}"]`);
      const variant = selector?.currentVariant;
      const wrappers = Array.from(selector?.querySelectorAll('[data-input-wrapper]') || []);
      let sizeIndex = wrappers.findIndex(wrapper => /size|talla|tamaño|taille|größe|grösse/i.test(wrapper.querySelector('input, select')?.name || ''));
      if (sizeIndex < 0 && variant?.options?.length === 1) sizeIndex = 0;
      const euSize = sizeIndex >= 0 ? variant?.options?.[sizeIndex] : null;
      const unit = window.fmSizeUnit || 'EU';
      const size = euSize && unit !== 'EU'
        ? window.fmSizeMappings?.[euSize]?.[unit] || euSize
        : euSize;
      const title = this.querySelector('[data-restock-title]');
      if (title && title.textContent !== copy.title(size)) title.textContent = copy.title(size);

      const globoButton = this.querySelector('#Globo-Back-In-Stock .gSubscribeBtn-Text');
      if (globoButton && globoButton.textContent.trim().toUpperCase() === 'SUBSCRIBE') {
        globoButton.textContent = copy.button;
      }
      const emailInput = this.querySelector('#Globo-Back-In-Stock input[type="email"]');
      if (emailInput && emailInput.getAttribute('aria-label') !== copy.email) emailInput.setAttribute('aria-label', copy.email);
      const feedback = {
        en: {
          'The email address is required': 'Enter your email address.',
          'The email address you entered is invalid': 'Enter a valid email address.',
          'Your notification has been registered': 'We’ll email you when it’s back in stock.',
          'Your email address is registered on this product': 'You’re already signed up for this item.'
        },
        es: {
          'The email address is required': 'Introduce tu correo electrónico.',
          'The email address you entered is invalid': 'Introduce un correo electrónico válido.',
          'Your notification has been registered': 'Te avisaremos cuando vuelva a estar disponible.',
          'Your email address is registered on this product': 'Ya estás apuntado al aviso de este artículo.'
        },
        fr: {
          'The email address is required': 'Saisissez votre adresse e-mail.',
          'The email address you entered is invalid': 'Saisissez une adresse e-mail valide.',
          'Your notification has been registered': 'Nous vous préviendrons dès son retour en stock.',
          'Your email address is registered on this product': 'Vous êtes déjà inscrit pour cet article.'
        },
        de: {
          'The email address is required': 'Gib deine E-Mail-Adresse ein.',
          'The email address you entered is invalid': 'Gib eine gültige E-Mail-Adresse ein.',
          'Your notification has been registered': 'Wir benachrichtigen dich, sobald der Artikel wieder verfügbar ist.',
          'Your email address is registered on this product': 'Du bist für diesen Artikel bereits angemeldet.'
        }
      };
      this.querySelectorAll('#Globo-Back-In-Stock .gBackInStock-messages, #Globo-Back-In-Stock .gBackInStock-Message').forEach(message => {
        const original = message.textContent.trim();
        const translated = feedback[this.dataset.restockLocale]?.[original] || feedback.en[original];
        if (translated && message.textContent !== translated) message.textContent = translated;
      });
      this.querySelectorAll('#Globo-Back-In-Stock .gBackInStock-messages').forEach(message => {
        if (!message.hasAttribute('role')) message.setAttribute('role', 'alert');
      });
      const successMessage = this.querySelector('#Globo-Back-In-Stock .gBackInStock-Message');
      if (successMessage && !successMessage.hasAttribute('role')) successMessage.setAttribute('role', 'status');
      if (!variant || this.dataset.available !== 'false') return;
      const variantInput = this.querySelector('#Globo-Back-In-Stock .gBackInStock-Form-Variant-Id');
      if (variantInput && variantInput.value !== String(variant.id)) variantInput.value = String(variant.id);
      if (variantInput && variantInput.getAttribute('value') !== String(variant.id)) variantInput.setAttribute('value', String(variant.id));
      const variantSelect = this.querySelector('#Globo-Back-In-Stock .gBackInStock-Form-Select');
      if (variantSelect && variantSelect.value !== String(variant.id)) variantSelect.value = String(variant.id);
    }

    onSubmitHandler(event) {
      event.preventDefault();
      if (this.submitButton.disabled || this.submitButton.classList.contains('disabled')) return;
      const purchaseOptions = document.querySelector(
        'purchase-options'
      );
      if (
        !!purchaseOptions &&
        !purchaseOptions.isOneTimePurchase() &&
        !this.hasAttribute('data-has-selling-plan')
      ) {
        purchaseOptions.showError();
        return;
      }

      this.submitButton.classList.add('disabled');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const formData = new FormData(this.form);
      formData.append(
        'sections',
        this.cartDrawer
          .getSectionsToRender()
          .map(section => section.section)
      );
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          this.cartDrawer.renderContents(response);
              this.cartDrawer.open();
  })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          this.submitButton.classList.remove('disabled');
        });
    }

    handleErrorMessage(errorMessage = false) {
      const errorWrapper = this.querySelector('[data-error-wrapper]');
      if (!errorWrapper) return;
      errorWrapper.classList.toggle('hidden', !errorMessage);
      errorWrapper.textContent = errorMessage || '';
    }

    toggleAddButton(disable, text) {
      this.dataset.available = String(!disable);
      this.querySelector('[data-back-in-stock]')?.toggleAttribute('hidden', !disable || text === window.variantStrings.unavailable);
      this.syncRestockContent();
      [
        this.submitButton,
        this.productBar?.querySelector('[name="add"]')
      ].forEach(submitButton => {
        if (disable) {
          submitButton?.setAttribute('disabled', 'disabled');
          if (text && submitButton) submitButton.textContent = text;
        } else if (submitButton) {
          submitButton.removeAttribute('disabled');
          if (
            this.form.querySelector(
              'input[name="properties[pre-order]"], input[name="properties[Pre-Order]"]'
            )
          ) {
            submitButton.textContent =
              window.variantStrings.preOrder || 'Pre-Order';
          } else {
            submitButton.textContent = this.hasAttribute(
              'data-has-selling-plan'
            )
              ? window.variantStrings.addSubscriptionToCart
              : window.variantStrings.addToCart;
          }
        }
      });
    }
  }

  customElements.define('product-form', ProductForm);
})();
